import { Component, DestroyRef, inject, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { CustomModalComponent } from '@daisi/components/custom-modal/custom-modal.component';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AlertComponent, FormFieldComponent, InputDirective } from '@ndwnu/design-system';
import { ContainsModal } from '@daisi/services/custom-modal/custom-modal.service';
import { FromToDateValidators } from '@daisi/shared/validators/from-to-date.validator';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IncidentReportControllerService } from '@generated/backend-api/api/incident-report-controller.service';

@Component({
  imports: [
    FormsModule,
    CustomModalComponent,
    FormFieldComponent,
    ReactiveFormsModule,
    InputDirective,
    AlertComponent,
  ],
  templateUrl: './incident-report-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './incident-report-modal.component.scss',
})
export class IncidentReportModalComponent implements ContainsModal {
  incidentReportController = inject(IncidentReportControllerService);
  destroyRef = inject(DestroyRef);

  maxPeriodLength = 14;

  readonly modalRef = viewChild.required<CustomModalComponent>('switchRoadOperatorModal');

  form = new FormGroup(
    {
      from: new FormControl(this.today, [Validators.required]),
      to: new FormControl(this.today, [Validators.required]),
    },
    {
      validators: [
        FromToDateValidators.dateBefore('from', 'to'),
        FromToDateValidators.maxPeriodLength(this.maxPeriodLength, 'from', 'to'),
      ],
    },
  );

  errorWhileDownloading = false;

  get today(): string {
    return new Date().toISOString().slice(0, 10);
  }

  downloadReport() {
    let formValue = this.form.value as unknown as { from: string; to: string };
    this.incidentReportController
      .downloadIncidentReport(formValue.from, formValue.to, 'response')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          // Extract filename from Content-Disposition header
          const contentDisposition = response.headers.get('Content-Disposition');
          let filename = 'incident-report.csv'; // Default filename

          if (contentDisposition) {
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            const matches = filenameRegex.exec(contentDisposition);
            if (matches != null && matches[1]) {
              filename = matches[1].replace(/['"]/g, '');
            }
          }
          // Create a blob from the response data
          const blob = new Blob([response.body as BlobPart], { type: 'text/csv' });

          // Create a download link and trigger it
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);

          this.modalRef().close();
        },
        error: (err) => {
          console.error(err);
          this.errorWhileDownloading = true;
          this.modalRef().isSubmitting.set(false);
        },
      });
  }
}
